import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { compact, isEqual, some, uniq } from 'lodash/fp';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Prompt } from 'react-router-dom';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import { AdminSectionData } from 'dashboard/components/PermissionsTable/AdminSectionData';
import { ADMIN_ROLE_NAME } from 'dashboard/components/PermissionsTable/constants';
import { DataSectionData } from 'dashboard/components/PermissionsTable/DataSectionData';
import { DevelopmentSectionData } from 'dashboard/components/PermissionsTable/DevelopmentSectionData';
import { ProductsSectionData } from 'dashboard/components/PermissionsTable/ProductsSectionData';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Backdrop, Container, SoloColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import { AccountType } from 'dashboard/models/account-type';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { List } from 'dashboard/models/list';
import { CustomerTier, DashboardNavbarSetting, Organization } from 'dashboard/models/organization';
import { ReportTemplate } from 'dashboard/models/report-template';
import { NavItem, Role, RolePermission } from 'dashboard/models/role';
import { TableView } from 'dashboard/models/table-view';
import { Team } from 'dashboard/models/team';
import { Template } from 'dashboard/models/template';
import { TransactionType } from 'dashboard/models/transaction-type';
import { User } from 'dashboard/models/user';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { Workflow } from 'dashboard/models/workflow';
import AddUserToRoleModal from 'dashboard/views/DashboardRolesShowView/AddUserToRoleModal';
import EnvironmentsAdvancedSettings from 'dashboard/views/DashboardRolesShowView/EnvironmentsAdvancedSettings';
import EnvironmentsTable from 'dashboard/views/DashboardRolesShowView/EnvironmentsTable';
import NavbarTables from 'dashboard/views/DashboardRolesShowView/NavbarTables';
import PermissionsTable from 'dashboard/views/DashboardRolesShowView/PermissionsTable';
import ProductsAdvancedSettings from 'dashboard/views/DashboardRolesShowView/ProductsAdvancedSettings';
import RoleHeader from 'dashboard/views/DashboardUserRolesShowView/RoleHeader';
import RoleUsersList from 'dashboard/views/DashboardUserRolesShowView/RoleUsersList';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { default as TabBar, Tab } from 'design-system/components/TabBar';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

enum TabName {
  ADMIN = 'Admin',
  DATA = 'Data',
  DEVELOPMENT = 'Development',
  ENVIRONMENTS = 'Environments',
  PRODUCTS = 'Products',
  USERS = 'Users',
  SIDEBAR = 'Sidebar',
}

interface DashboardRolesShowViewProps {
  addUserToRoleSearchUsers: User[];
  caseAssignmentPolicies: CaseAssignmentPolicy[];
  caseTemplates: CaseTemplate[];
  caseQueues: CaseQueue[];
  currentUser: User;
  environments: Environment[];
  externalIntegrations: ExternalIntegration[];
  inquiryTemplates: (InquiryTemplate | Template)[];
  isLoading: boolean;
  lists: List[];
  loadingRoleUsers: boolean;
  organization: Organization;
  productFeatureIsDisabled: boolean;
  reportTemplates: ReportTemplate[];
  role: Role;
  roles: Role[];
  accountTypes: AccountType[];
  transactionTypes: TransactionType[];
  tableViews: TableView<any>[];
  teams: Team[];
  roleUsers: User[];
  usersListSearchTerm: string;
  verificationTemplates: VerificationTemplate[];
  workflows: Workflow[];
  pageSize: number;
  paginationMeta: PaginationParams;
  readmeJwtToken: string;
  handleSubmitAddUser: (userIds: string[]) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  setAddUserToRoleSearchTerm: (term: string) => void;
  setUserSearchTerm: (term: string) => void;
  removeUserFromRole: ({ user, role }: { user: User; role: Role }) => Promise<void>;
  updateRole: (
    role: Role,
    rolePermissions: RolePermission[],
    navItems: NavItem[],
    name: string,
    description: string,
  ) => Promise<void>;
  destroyRole: (roleId: string) => Promise<void>;
}

const DashboardRolesShowView = ({
  addUserToRoleSearchUsers,
  caseAssignmentPolicies,
  caseTemplates,
  caseQueues,
  currentUser,
  environments,
  externalIntegrations,
  inquiryTemplates,
  isLoading,
  lists,
  loadingRoleUsers,
  organization,
  productFeatureIsDisabled,
  reportTemplates,
  role,
  roles,
  tableViews,
  teams,
  accountTypes,
  transactionTypes,
  roleUsers,
  usersListSearchTerm,
  verificationTemplates,
  workflows,
  setUserSearchTerm,
  removeUserFromRole,
  updateRole,
  pageSize,
  paginationMeta,
  setAddUserToRoleSearchTerm,
  handlePageSizeClick,
  handlePageLinkClick,
  handleSubmitAddUser,
  destroyRole,
  readmeJwtToken,
}: DashboardRolesShowViewProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [description, setDescription] = useState<string>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [roleName, setRoleName] = useState<string>(null);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [userToRemove, setUserToRemove] = useState<User>(undefined);
  const [selectedTab, setSelectedTab] = useState<TabName>(TabName.USERS);

  const existingRolePermissions = role?.attributes?.['role-permissions'];
  const existingName = role?.attributes?.name;
  const existingNavItems = role?.attributes?.preferences['enabled-nav-items'];
  const existingDescription = role?.attributes?.description;

  const [showAddUserModal, { enable: openAddUserModal, disable: closeAddUserModal }] =
    useToggle(false);

  const [
    deactivateRoleModal,
    { enable: showDeactivateRoleModal, disable: hideDeactivateRoleModal },
  ] = useToggle(false);

  const shownRoleIsAdmin = role?.attributes?.name === ADMIN_ROLE_NAME;
  const canEditRoleAttributes = currentUser.attributes['is-admin'] && !productFeatureIsDisabled;
  const hasRoleAssignUserPermission = usePermission(Permission.RoleAssignUser);
  const canAssignRoleUsers = canEditRoleAttributes || hasRoleAssignUserPermission;

  const hasRoleBasedNavbarSetting =
    organization.attributes['dashboard-navbar-setting'] === DashboardNavbarSetting.RoleBased;

  const allCurrentAndFutureEnvironmentSelected = useMemo(() => {
    return some(
      (rp) => rp.name === Permission.EnvironmentView && rp['environment-id'] == null,
      rolePermissions,
    );
  }, [rolePermissions]);

  const selectedEnvironmentIds = useMemo(() => {
    const environmentViewRolePermissionIds = rolePermissions
      .filter((rp) => rp.name === Permission.EnvironmentView && rp['environment-id'] != null)
      .map((rp) => rp['environment-id']);

    // TODO: (jchang) remove this second option once we're using environment.view
    return environmentViewRolePermissionIds.length > 0
      ? environmentViewRolePermissionIds
      : compact(uniq(rolePermissions.map((rp) => rp['environment-id'])));
  }, [rolePermissions]);

  useEffect(() => {
    setRoleName(existingName);
    setRolePermissions(existingRolePermissions || []);
    setNavItems(existingNavItems || []);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setDescription(existingDescription);
  }, [existingName, existingRolePermissions, existingNavItems, existingDescription]);

  const permissionsDirty = useMemo(() => {
    const comparator = (a: RolePermission, b: RolePermission) =>
      a.name > b.name ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      a['environment-id'] > b['environment-id'] ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      a['object-id'] > b['object-id']
        ? 1
        : -1;

    return !isEqual(
      (existingRolePermissions || []).sort(comparator),
      rolePermissions.sort(comparator),
    );
  }, [existingRolePermissions, rolePermissions]);

  const navItemsDirty = useMemo(() => {
    return !isEqual((existingNavItems || []).sort(), navItems.sort());
  }, [existingNavItems, navItems]);

  const isDirty = !isLoading && (roleName !== existingName || permissionsDirty || navItemsDirty);

  if (isLoading) {
    return <LoadingIndicator fullscreen={true} />;
  } else if (role == null) {
    return (
      <Backdrop>
        <NullState message="We couldn't find that role." backRoute='/dashboard/user-roles' />
      </Backdrop>
    );
  }

  const handleUpdateRole = async () => {
    await updateRole(role, rolePermissions, navItems, roleName, description);
  };

  /*
   * Need to filter these for presence due to pagination. These relationships
   * aren't paginated at the moment.
   */
  const users = compact(
    role.relationships.users.data.map((user) =>
      roleUsers.find((apiModel) => apiModel.id === user.id),
    ),
  );

  const mayEditPermissions =
    selectedEnvironmentIds.length > 0 || allCurrentAndFutureEnvironmentSelected;

  return (
    <>
      <Helmet title={`${roleName} - Roles`} />
      <RoleHeader
        title={roleName}
        role={role}
        isDirty={isDirty}
        canUpsell={environments.length > 0 && productFeatureIsDisabled}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        canEditRoleAttributes={!shownRoleIsAdmin && canEditRoleAttributes}
        onUpdateRole={handleUpdateRole}
        setRoleName={setRoleName}
        showDeactivateRoleModal={showDeactivateRoleModal}
      />
      <Backdrop>
        <Container>
          <Prompt
            when={isDirty}
            message='You have unsaved changes. Are you sure you want to leave?'
          />
          <SoloColumn>
            <TabBar>
              <Tab
                id='user-roles-show__users-tab'
                selected={selectedTab === TabName.USERS}
                onClick={() => setSelectedTab(TabName.USERS)}
              >
                {TabName.USERS}
              </Tab>
              <Tab
                id='user-roles-show__admin-tab'
                selected={selectedTab === TabName.ADMIN}
                onClick={() => setSelectedTab(TabName.ADMIN)}
              >
                {TabName.ADMIN}
              </Tab>
              <Tab
                id='user-roles-show__environments-tab'
                selected={selectedTab === TabName.ENVIRONMENTS}
                onClick={() => setSelectedTab(TabName.ENVIRONMENTS)}
              >
                {TabName.ENVIRONMENTS}
                {canEditRoleAttributes &&
                  !shownRoleIsAdmin &&
                  selectedEnvironmentIds.length === 0 &&
                  !allCurrentAndFutureEnvironmentSelected && (
                    <>
                      <Spacer width={0.5} />
                      <Tooltip side='right' iconTrigger={<CircleAlertIcon />}>
                        This role does not have any environments enabled.
                      </Tooltip>
                    </>
                  )}
              </Tab>
              <Tab
                id='user-roles-show__products-tab'
                selected={selectedTab === TabName.PRODUCTS}
                onClick={() => setSelectedTab(TabName.PRODUCTS)}
              >
                {TabName.PRODUCTS}
              </Tab>
              <Tab
                id='user-roles-show__data-tab'
                selected={selectedTab === TabName.DATA}
                onClick={() => setSelectedTab(TabName.DATA)}
              >
                {TabName.DATA}
              </Tab>
              <Tab
                id='user-roles-show__development-tab'
                selected={selectedTab === TabName.DEVELOPMENT}
                onClick={() => setSelectedTab(TabName.DEVELOPMENT)}
              >
                {TabName.DEVELOPMENT}
              </Tab>
              {hasRoleBasedNavbarSetting && (
                <Tab
                  id='user-roles-show__navbar-tab'
                  selected={selectedTab === TabName.SIDEBAR}
                  onClick={() => setSelectedTab(TabName.SIDEBAR)}
                >
                  {TabName.SIDEBAR}
                </Tab>
              )}
            </TabBar>
            <Spacer height={2} />
            {!canEditRoleAttributes &&
              organization.attributes['customer-tier'] !== CustomerTier.Tier3 && (
                <>
                  <StyledMessage>
                    <Paragraph color='primary'>
                      Only users with the Admin role can edit other roles&apos; permissions. Please
                      contact your Admin(s) to make permission changes to this role.
                    </Paragraph>
                  </StyledMessage>
                  <Spacer height={2} />
                </>
              )}
            {selectedTab === TabName.USERS && (
              <>
                <RoleUsersList
                  canAssignRoleUsers={canAssignRoleUsers}
                  currentUser={currentUser}
                  loadingRoleUsers={loadingRoleUsers}
                  roles={roles}
                  roleUsers={users}
                  userSearchTerm={usersListSearchTerm}
                  openAddUserModal={openAddUserModal}
                  setUserSearchTerm={setUserSearchTerm}
                  setUserToRemove={setUserToRemove}
                />
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationMeta}
                  handlePageLinkClick={handlePageLinkClick}
                  handlePageSizeClick={handlePageSizeClick}
                />
                <ModalWrapper isOpen={showAddUserModal} handleCloseModal={closeAddUserModal}>
                  <AddUserToRoleModal
                    role={role}
                    users={addUserToRoleSearchUsers}
                    handleClose={closeAddUserModal}
                    setSearchTerm={setAddUserToRoleSearchTerm}
                    handleSubmit={handleSubmitAddUser}
                  />
                </ModalWrapper>
                <ModalWrapper
                  isOpen={userToRemove != null}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleCloseModal={() => setUserToRemove(null)}
                >
                  <ConfirmationModal
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    cancelAction={() => setUserToRemove(null)}
                    confirmAction={() => removeUserFromRole({ role, user: userToRemove })}
                    header={`Remove user from ${role.attributes.name}?`}
                    confirmActionText='Remove'
                  >
                    Are you sure you want to remove{' '}
                    <strong>{userToRemove?.attributes['email-address']}</strong>?
                  </ConfirmationModal>
                </ModalWrapper>
              </>
            )}
            {selectedTab === TabName.ADMIN && (
              <>
                {canEditRoleAttributes && (
                  <>
                    <StyledMessage>
                      <Paragraph color='primary'>
                        Permissions on this tab apply across the organization, in all current and
                        future environments you may create. (For more details, see this{' '}
                        <StyledLink
                          href='https://support.withpersona.com/hc/en-us/articles/6794627143187'
                          target='_blank'
                          rel='noreferrer'
                        >
                          guide
                        </StyledLink>
                        .)
                      </Paragraph>
                    </StyledMessage>
                    <Spacer height={2} />
                  </>
                )}
                <Card>
                  <Card.Body>
                    <Card.Title>
                      <Heading size='sm'>System</Heading>
                    </Card.Title>
                    <PermissionsTable
                      disabled={!canEditRoleAttributes || shownRoleIsAdmin}
                      isAdmin={shownRoleIsAdmin}
                      objects={[...externalIntegrations, ...teams]}
                      rolePermissions={rolePermissions}
                      schema={AdminSectionData}
                      sectionTitle='System'
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      selectedEnvironmentIds={selectedEnvironmentIds}
                      setRolePermissions={setRolePermissions}
                    />
                  </Card.Body>
                </Card>
              </>
            )}
            {selectedTab === TabName.ENVIRONMENTS && (
              <Card>
                <Card.Body>
                  <Card.Title>
                    <Heading size='sm'>{TabName.ENVIRONMENTS}</Heading>
                  </Card.Title>
                  <EnvironmentsTable
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin}
                    environments={environments}
                    isAdmin={shownRoleIsAdmin}
                    rolePermissions={rolePermissions}
                    allCurrentAndFutureEnvironmentSelected={allCurrentAndFutureEnvironmentSelected}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedEnvironmentIds={selectedEnvironmentIds}
                    setRolePermissions={setRolePermissions}
                  />
                  <Spacer height={2} />
                  <EnvironmentsAdvancedSettings
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin}
                    environments={environments}
                    isAdmin={shownRoleIsAdmin}
                    allCurrentAndFutureEnvironmentSelected={allCurrentAndFutureEnvironmentSelected}
                    rolePermissions={rolePermissions}
                    setRolePermissions={setRolePermissions}
                  />
                </Card.Body>
              </Card>
            )}
            {selectedTab === TabName.PRODUCTS && (
              <Card>
                <Card.Body>
                  <Card.Title>
                    <Heading size='sm'>{TabName.PRODUCTS}</Heading>
                  </Card.Title>
                  <PermissionsTable
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin || !mayEditPermissions}
                    isAdmin={shownRoleIsAdmin}
                    objects={[
                      ...caseAssignmentPolicies,
                      ...caseTemplates,
                      ...caseQueues,
                      ...inquiryTemplates,
                      ...lists,
                      ...reportTemplates,
                      ...tableViews,
                      ...teams,
                      ...accountTypes,
                      ...transactionTypes,
                      ...verificationTemplates,
                      ...workflows,
                    ]}
                    rolePermissions={rolePermissions}
                    schema={ProductsSectionData}
                    sectionTitle='Product'
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedEnvironmentIds={selectedEnvironmentIds}
                    setRolePermissions={setRolePermissions}
                  />
                  <Spacer height={2} />
                  <ProductsAdvancedSettings
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin || !mayEditPermissions}
                    isAdmin={shownRoleIsAdmin}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedEnvironmentIds={selectedEnvironmentIds}
                    rolePermissions={rolePermissions}
                    setRolePermissions={setRolePermissions}
                  />
                </Card.Body>
              </Card>
            )}
            {selectedTab === TabName.DATA && (
              <Card>
                <Card.Body>
                  <Card.Title>
                    <Heading size='sm'>{TabName.DATA}</Heading>
                  </Card.Title>
                  <PermissionsTable
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin || !mayEditPermissions}
                    isAdmin={shownRoleIsAdmin}
                    rolePermissions={rolePermissions}
                    schema={DataSectionData}
                    sectionTitle='Type'
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedEnvironmentIds={selectedEnvironmentIds}
                    setRolePermissions={setRolePermissions}
                  />
                </Card.Body>
              </Card>
            )}
            {selectedTab === TabName.DEVELOPMENT && (
              <Card>
                <Card.Body>
                  <Card.Title>
                    <Heading size='sm'>{TabName.DEVELOPMENT}</Heading>
                  </Card.Title>
                  <PermissionsTable
                    disabled={!canEditRoleAttributes || shownRoleIsAdmin || !mayEditPermissions}
                    isAdmin={shownRoleIsAdmin}
                    rolePermissions={rolePermissions}
                    schema={DevelopmentSectionData}
                    sectionTitle='System'
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    selectedEnvironmentIds={selectedEnvironmentIds}
                    setRolePermissions={setRolePermissions}
                  />
                </Card.Body>
              </Card>
            )}
            {selectedTab === TabName.SIDEBAR && (
              <NavbarTables
                disabled={!canEditRoleAttributes || shownRoleIsAdmin || !mayEditPermissions}
                shownRoleIsAdmin={shownRoleIsAdmin}
                enabledNavItems={navItems}
                readmeJwtToken={readmeJwtToken}
                setEnabledNavItems={setNavItems}
              ></NavbarTables>
            )}
          </SoloColumn>
        </Container>
      </Backdrop>
      <ModalWrapper isOpen={deactivateRoleModal} handleCloseModal={hideDeactivateRoleModal}>
        <ModalContainer data-test='deactivate-role-modal'>
          <ModalHeader data-test='deactivate-role-modal__header' onClose={hideDeactivateRoleModal}>
            {`Deactivate ${role.attributes.name}?`}
          </ModalHeader>

          <ModalBody>
            <HStack equal={true}>
              <Button
                id='deactivate-role-form__close'
                variant='basic'
                onClick={hideDeactivateRoleModal}
              >
                Cancel
              </Button>
              <Button
                id='deactivate-role-form__submit'
                type='submit'
                onClick={() => {
                  destroyRole(role.id);
                  hideDeactivateRoleModal();
                }}
              >
                Deactivate
              </Button>
            </HStack>
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

export default DashboardRolesShowView;

const StyledMessage = styled.div`
  display: grid;
  grid-template-columns: 1fr 15%;
  align-items: center;
  padding: ${Spacing[3]} ${Spacing[5]};
  border: 1px solid ${Color.CoreYellow300};
  background-color: ${Color.CoreYellow100};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.color.text.primary};
  font-weight: ${FontWeight.Medium};
`;
